import { useState } from "react";
import { Download, Loader2, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useRoleFlags } from "@/hooks/use-role-flags";
import { toast } from "sonner";

interface DownloadImageButtonProps {
  deviceId: string;
  item: {
    deviceLastUpdated?: string;
    imageWatermarkFree?: string | { url?: string } | null;
    imageKit?: {
      url?: string;
      imageWatermarkFree?: string;
      watermarkFreeUrl?: string;
    };
  };
  compact?: boolean;
}

const PROXIED_HOSTS = new Set([
  "storage.googleapis.com",
  "snapshot-live-v1.firebasestorage.app",
  "firebasestorage.googleapis.com",
  "image.snapshot.systems",
]);

const proxied = (url: string): string => {
  try {
    if (!PROXIED_HOSTS.has(new URL(url).hostname)) return url;
  } catch {
    return url;
  }
  const base = import.meta.env.VITE_SUPABASE_URL || "https://ndqudoynysmynztrepwj.supabase.co";
  return `${base}/functions/v1/image-proxy?url=${encodeURIComponent(url)}`;
};

const stamp = (ts?: string) => {
  const d = ts ? new Date(ts) : new Date();
  return isNaN(d.getTime()) ? "image" : d.toISOString().replace(/[:.]/g, "-").slice(0, 19);
};

const extFrom = (url: string, type: string) => {
  if (type.includes("png")) return "png";
  if (type.includes("webp")) return "webp";
  const m = url.split("?")[0].match(/\.(jpe?g|png|webp)$/i);
  return m ? m[1].toLowerCase().replace("jpeg", "jpg") : "jpg";
};

export const DownloadImageButton = ({ deviceId, item, compact }: DownloadImageButtonProps) => {
  const { isSuperuser, isAdmin } = useRoleFlags();
  const [busy, setBusy] = useState(false);
  const standardUrl = item.imageKit?.url;
  const fullUrl =
    item.imageKit?.imageWatermarkFree ||
    (typeof item.imageWatermarkFree === "string"
      ? item.imageWatermarkFree
      : item.imageWatermarkFree?.url) ||
    item.imageKit?.watermarkFreeUrl;

  if (!standardUrl) return null;

  const download = async (url: string, suffix: string) => {
    setBusy(true);
    try {
      const res = await fetch(proxied(url));
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const name = `${deviceId}_${stamp(item.deviceLastUpdated)}${suffix}.${extFrom(url, blob.type)}`;
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
    } catch (e) {
      console.error("Image download failed:", e);
      toast.error(e instanceof Error ? e.message : "Failed to download image");
    } finally {
      setBusy(false);
    }
  };

  const icon = busy ? (
    <Loader2 className={`${compact ? "h-3 w-3" : "h-4 w-4"} animate-spin`} />
  ) : (
    <Download className={compact ? "h-3 w-3" : "h-4 w-4"} />
  );
  const cls = compact ? "h-7 gap-1 text-xs" : "gap-2";

  if (!(isSuperuser || isAdmin)) {
    return (
      <Button
        variant="outline"
        size="sm"
        className={cls}
        disabled={busy}
        onClick={(e) => {
          e.stopPropagation();
          download(standardUrl, "");
        }}
      >
        {icon}
        Download
      </Button>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cls}
          disabled={busy}
          onClick={(e) => e.stopPropagation()}
        >
          {icon}
          Download
          <ChevronDown className={compact ? "h-3 w-3" : "h-4 w-4"} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-[100]">
        <DropdownMenuItem onSelect={() => download(standardUrl, "")}>Standard image</DropdownMenuItem>
        <DropdownMenuItem
          disabled={!fullUrl}
          onSelect={() => fullUrl && download(fullUrl, "_full")}
          className="flex flex-col items-start"
        >
          <span>Watermark-free</span>
          {!fullUrl && (
            <span className="text-[10px] text-muted-foreground">Not available for this image</span>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default DownloadImageButton;
